import React, { useEffect, useState } from "react";
import { Select, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { getLog } from "./api";
import {
  STATUS_COLORS,
  channelLabels,
  describeReason,
  formatDateTime,
  purposeLabels,
  statusLabels
} from "./labels";

const PAGE_SIZE = 25;

const MessageLog = () => {
  const { t, i18n } = useTranslation("dashboard");
  const purposes = purposeLabels(t);
  const statuses = statusLabels(t);
  const channels = channelLabels(t);
  const [filters, setFilters] = useState({ status: undefined, purpose: undefined });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], count: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLog({ ...filters, page: page - 1, pageSize: PAGE_SIZE })
      .then(res => active && setData(res.data))
      .catch(generalErrorAlert)
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [filters, page]);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const columns = [
    {
      title: t("Message"),
      key: "purpose",
      mobile: "title",
      render: (_, row) => (
        <div>
          <Typography.Text strong>{purposes[row.purpose] || row.purpose}</Typography.Text>
          <div className="nt-small">{row.clientName || row.to || "—"}</div>
        </div>
      )
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 120,
      render: (_, row) => <Tag color={STATUS_COLORS[row.status]}>{statuses[row.status]}</Tag>
    },
    {
      title: t("Channel"),
      key: "channel",
      width: 150,
      render: (_, row) =>
        row.channel ? (
          <span>
            {channels[row.channel] || row.channel}
            {row.provider ? <span className="nt-small"> · {row.provider}</span> : null}
          </span>
        ) : (
          "—"
        )
    },
    {
      title: t("Details"),
      key: "reason",
      mobile: "full",
      render: (_, row) => (
        <Typography.Paragraph
          className="nt-small nt-wrap"
          ellipsis={{ rows: 2, expandable: true, symbol: t("more") }}
        >
          {row.status === "failed" ? row.reason : describeReason(row.reason, t) || row.text}
        </Typography.Paragraph>
      )
    },
    {
      title: t("Date"),
      key: "createdAt",
      width: 150,
      render: (_, row) => formatDateTime(row.sentAt || row.createdAt, i18n.language)
    }
  ];

  return (
    <div>
      <PageToolbar
        filters={
          <Space wrap className="nt-full">
            <Select
              allowClear
              className="nt-filter"
              placeholder={t("All messages")}
              value={filters.purpose}
              onChange={value => setFilter("purpose", value)}
              options={Object.entries(purposes).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              className="nt-filter"
              placeholder={t("All statuses")}
              value={filters.status}
              onChange={value => setFilter("status", value)}
              options={Object.entries(statuses).map(([value, label]) => ({ value, label }))}
            />
          </Space>
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={data.data}
        mobileAvatar={false}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data.count,
          showSizeChanger: false,
          onChange: setPage
        }}
      />
    </div>
  );
};

export default MessageLog;
